import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const ReprintButton = () => {
  const [searchParams] = useSearchParams();
  const [labInfo, setLabInfo] = useState(null);
  const [previewSaved, setPreviewSaved] = useState(false);
  const [previewSaveError, setPreviewSaveError] = useState(null);
  const [previewSaveMessage, setPreviewSaveMessage] = useState("");

  const accountIds = useMemo(() => {
    const ids = searchParams.get("accountIds");
    return ids
      ? ids
          .split(",")
          .map((id) => parseInt(id.trim(), 10))
          .filter((id) => !isNaN(id))
      : [];
  }, [searchParams]);

  const providerIds = useMemo(() => {
    const ids = searchParams.get("providerIds");
    return ids ? ids.split(",").filter(id => id && id.trim() !== '') : [];
  }, [searchParams]);

  const dateParam = searchParams.get("date");
  const lastRunTimeParam = searchParams.get("lastRunTime");
  const selectedDateParam = searchParams.get("selectedDate");
  const displayDate = selectedDateParam || (dateParam ? new Date(dateParam).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
  const cutoffDateTime = dateParam || new Date().toISOString();
  const lastRunTime = lastRunTimeParam || null;
  const statementNote = decodeURIComponent(searchParams.get("statementNote") || "");
  const statementFooter = decodeURIComponent(searchParams.get("statementFooter") || "");
  const dontPrintEbilling = searchParams.get("dontPrintEbilling") === "true";
  const dontPrintZeroBalance = searchParams.get("dontPrintZeroBalance") === "true";
  const autoSaveStatements = searchParams.get("autoSaveStatements") === "true";

  const [statements, setStatements] = useState([]);
  const [filteredStatements, setFilteredStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [providerMap, setProviderMap] = useState({});
  const hideZeroBalanceInPrint = dontPrintZeroBalance === true;

  // Helper functions
  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = str.replace(/^0+/, '');
    return result === '' ? '0' : result;
  };

  const formatCredit = (amount) => {
    if (!amount || Number(amount) === 0) return "";
    return `-£${Math.abs(Number(amount)).toFixed(2)}`;
  };

  const formatDebit = (amount) => {
    if (!amount || Number(amount) === 0) return "";
    const num = Number(amount);
    return num < 0 ? `-£${Math.abs(num).toFixed(2)}` : `£${num.toFixed(2)}`;
  };

  const calculateDaysUnpaid = (invoiceDateStr, statementDateStr) => {
    if (!invoiceDateStr || !statementDateStr) return 0;
    const invDate = new Date(invoiceDateStr);
    const stmtDate = new Date(statementDateStr);
    if (isNaN(invDate.getTime()) || isNaN(stmtDate.getTime())) return 0;
    const diffTime = stmtDate - invDate;
    return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };

  // NEW FUNCTION: Group transactions by provider with proper handling
  const groupTransactionsByProvider = (transactions, invoiceLookup) => {
    const providerGroups = {};

    transactions.forEach(trans => {
      // Find which provider this transaction belongs to
      let providerId = 'unknown';
      let providerName = 'Unknown Provider';
      let foundProvider = false;

      // Try to find provider from invoice lookup
      if (trans.invoiceNumber && invoiceLookup[trans.invoiceNumber]) {
        providerId = invoiceLookup[trans.invoiceNumber].providerId || 'unknown';
        providerName = invoiceLookup[trans.invoiceNumber].providerName || 'Unknown Provider';
        foundProvider = true;
      }

      // If not found, try to find from invoice numbers array
      if (!foundProvider && trans.invoiceNumbers && trans.invoiceNumbers.size > 0) {
        for (const invNum of trans.invoiceNumbers) {
          if (invoiceLookup[invNum]) {
            providerId = invoiceLookup[invNum].providerId || 'unknown';
            providerName = invoiceLookup[invNum].providerName || 'Unknown Provider';
            foundProvider = true;
            break;
          }
        }
      }

      const key = `${providerId}_${trans.date}_${trans.description}`;

      if (!providerGroups[key]) {
        providerGroups[key] = {
          ...trans,
          providerId,
          providerName,
          amount: 0,
          invoiceNumbers: new Set(),
          patientNames: new Set()
        };
      }

      providerGroups[key].amount += Math.abs(trans.amount || 0);

      if (trans.invoiceNumber) {
        providerGroups[key].invoiceNumbers.add(trans.invoiceNumber);
      }

      if (trans.patientName) {
        providerGroups[key].patientNames.add(trans.patientName);
      }
    });

    // Format the groups
    return Object.values(providerGroups).map(group => {
      const invoiceNumbers = Array.from(group.invoiceNumbers);
      const patientNames = Array.from(group.patientNames);

      let displayDescription = group.description;

      if (invoiceNumbers.length === 1 && patientNames.length === 1) {
        displayDescription = `Case Invoice ${removeLeadingZeros(invoiceNumbers[0])} / ${patientNames[0]} - ${group.type === 'payment' ? 'Payment' : 'Credit'}`;
      } else if (invoiceNumbers.length > 0) {
        const formattedInvoices = invoiceNumbers.map(inv => removeLeadingZeros(inv)).join(', ');
        displayDescription = `${group.type === 'payment' ? 'Payment' : 'Credit'} for Invoices: ${formattedInvoices}`;
      }

      return {
        ...group,
        displayDescription,
        invoiceNumbers,
        patientNames
      };
    });
  };

  const savePreviewToDatabase = useCallback(async (statementsData) => {
    if (!statementsData || statementsData.length === 0) {
      return;
    }
    try {
      setPreviewSaveMessage("Saving preview...");

      const response = await axios.post(
        "https://hms.backend.hiragroupcloud.com/api/statements/preview/save",
        {
          accountIds: searchParams.get("accountIds") || "",
          providerIds: searchParams.get("providerIds") || "",
          date: cutoffDateTime,
          lastRunTime: lastRunTime,
          dontPrintEbilling,
          dontPrintZeroBalance,
          statementNote,
          statementFooter,
          statements: statementsData
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        setPreviewSaved(true);
        setPreviewSaveError(null);

        if (response.data.isExisting) {
          setPreviewSaveMessage("Preview already exists in database");
        } else {
          setPreviewSaveMessage("Preview saved successfully");
        }

        setTimeout(() => {
          setPreviewSaveMessage("");
        }, 3000);
      }
    } catch (error) {
      console.error("Failed to save preview:", error);
      setPreviewSaveError(error.response?.data?.message || "Failed to save preview");
      setPreviewSaveMessage("");

      setTimeout(() => {
        setPreviewSaveError(null);
      }, 5000);
    }
  }, [searchParams, cutoffDateTime, lastRunTime, dontPrintEbilling,
      dontPrintZeroBalance, statementNote, statementFooter]);

 // Modified frontend: processStatements function
 const processStatements = useCallback((rawStatements) => {
   return rawStatements.map(statement => {
     const invoiceLookup = {};
     let totalDebits = 0;
     let totalCredits = 0;

     // Track provider-specific totals
     const providerTotals = {};

     // Process previous balance invoices
     statement.previousBalanceDetails?.forEach(providerDetail => {
       const providerKey = providerDetail.providerId || 'unknown';

       if (!providerTotals[providerKey]) {
         providerTotals[providerKey] = {
           providerId: providerDetail.providerId,
           providerName: providerDetail.providerName || 'Unknown Provider',
           previousBalance: 0,
           newBalance: 0,
           totalDebits: 0,
           totalCredits: 0,
           invoices: [],
           transactions: [],
           oneTo30Days: 0,
           thirtyOneTo60Days: 0,
           sixtyOneTo90Days: 0,
           ninetyPlusDays: 0
         };
       }

       providerDetail.invoices?.forEach(inv => {
         if (inv.invoiceNumber) {
           const previousOutstanding = inv.outstanding || inv.amount || 0;
           invoiceLookup[inv.invoiceNumber] = {
             ...inv,
             providerId: providerDetail.providerId,
             providerName: providerDetail.providerName,
             isPreviousBalance: true,
             originalAmount: previousOutstanding,
             debit: previousOutstanding,
             outstanding: inv.outstanding || 0,
             payments: [],
             credits: []
           };

           providerTotals[providerKey].previousBalance += previousOutstanding;
           providerTotals[providerKey].totalDebits += previousOutstanding;
           providerTotals[providerKey].invoices.push(invoiceLookup[inv.invoiceNumber]);
           totalDebits += previousOutstanding;

           // Add to provider aging
           const days = calculateDaysUnpaid(inv.invoiceDate, statement.cutoffDateTime);
           const outstanding = inv.outstanding || 0;
           if (outstanding > 0) {
             if (days <= 30) providerTotals[providerKey].oneTo30Days += outstanding;
             else if (days <= 60) providerTotals[providerKey].thirtyOneTo60Days += outstanding;
             else if (days <= 90) providerTotals[providerKey].sixtyOneTo90Days += outstanding;
             else providerTotals[providerKey].ninetyPlusDays += outstanding;
           }
         }
       });
     });

     // Process current invoices
     statement.invoices?.forEach(inv => {
       if (inv.invoiceNumber) {
         const debitAmount = inv.debit || inv.total || inv.amount || 0;
         const providerKey = inv.providerId || 'unknown';

         if (!providerTotals[providerKey]) {
           providerTotals[providerKey] = {
             providerId: inv.providerId,
             providerName: inv.providerName || 'Unknown Provider',
             previousBalance: 0,
             newBalance: 0,
             totalDebits: 0,
             totalCredits: 0,
             invoices: [],
             transactions: [],
             oneTo30Days: 0,
             thirtyOneTo60Days: 0,
             sixtyOneTo90Days: 0,
             ninetyPlusDays: 0
           };
         }

         invoiceLookup[inv.invoiceNumber] = {
           ...inv,
           isPreviousBalance: false,
           originalAmount: debitAmount,
           debit: debitAmount,
           outstanding: inv.outstanding || 0,
           payments: [],
           credits: [],
           providerId: inv.providerId,
           providerName: inv.providerName || 'Unknown Provider'
         };

         providerTotals[providerKey].totalDebits += debitAmount;
         providerTotals[providerKey].invoices.push(invoiceLookup[inv.invoiceNumber]);
         totalDebits += debitAmount;

         // Add to provider aging
         const days = calculateDaysUnpaid(inv.invoiceDate || inv.deliveryDate, statement.cutoffDateTime);
         const outstanding = inv.outstanding || 0;
         if (outstanding > 0) {
           if (days <= 30) providerTotals[providerKey].oneTo30Days += outstanding;
           else if (days <= 60) providerTotals[providerKey].thirtyOneTo60Days += outstanding;
           else if (days <= 90) providerTotals[providerKey].sixtyOneTo90Days += outstanding;
           else providerTotals[providerKey].ninetyPlusDays += outstanding;
         }
       }
     });

     // Process payments
     const allPaymentItems = [];
     statement.payments?.forEach(payment => {
       if (payment.appliedInvoices && Array.isArray(payment.appliedInvoices)) {
         payment.appliedInvoices.forEach(applied => {
           if (applied.invoiceNumber && invoiceLookup[applied.invoiceNumber]) {
             const inv = invoiceLookup[applied.invoiceNumber];
             const paymentAmount = applied.appliedAmount || payment.amount;

             inv.outstanding = Math.max(0, inv.outstanding - paymentAmount);

             const paymentItem = {
               date: payment.paymentDate,
               amount: paymentAmount,
               description: payment.description || `Payment`,
               type: 'payment',
               invoiceNumber: applied.invoiceNumber,
               patientName: getPatientName(inv)
             };
             inv.payments.push(paymentItem);
             allPaymentItems.push(paymentItem);

             // Update provider totals
             if (inv.providerId && providerTotals[inv.providerId]) {
               providerTotals[inv.providerId].totalCredits += paymentAmount;
             }
             totalCredits += paymentAmount;
           } else if (applied.invoiceNumber) {
             const paymentItem = {
               date: payment.paymentDate,
               amount: applied.appliedAmount || payment.amount,
               description: `Payment for Invoice ${removeLeadingZeros(applied.invoiceNumber)}`,
               type: 'payment',
               invoiceNumber: applied.invoiceNumber,
               patientName: ''
             };
             allPaymentItems.push(paymentItem);

             // Try to find provider for this invoice
             if (invoiceLookup[applied.invoiceNumber]) {
               const providerId = invoiceLookup[applied.invoiceNumber].providerId;
               if (providerId && providerTotals[providerId]) {
                 providerTotals[providerId].totalCredits += paymentItem.amount;
               }
             }
             totalCredits += paymentItem.amount;
           }
         });
       }
     });

     // Process credit memos
     const allCreditItems = [];
     statement.creditMemos?.forEach(creditMemo => {
       if (creditMemo.appliedInvoices && Array.isArray(creditMemo.appliedInvoices)) {
         creditMemo.appliedInvoices.forEach(applied => {
           if (applied.invoiceNumber) {
             const invoice = invoiceLookup[applied.invoiceNumber];
             const creditAmount = applied.appliedAmount || Math.abs(creditMemo.amount);

             if (invoice) {
               invoice.outstanding = Math.max(0, invoice.outstanding - creditAmount);

               const creditItem = {
                 date: creditMemo.creditDate,
                 amount: creditAmount,
                 description: `Credit`,
                 type: 'credit',
                 invoiceNumber: applied.invoiceNumber,
                 patientName: getPatientName(invoice)
               };
               invoice.credits.push(creditItem);
               allCreditItems.push(creditItem);

               // Update provider totals
               if (invoice.providerId && providerTotals[invoice.providerId]) {
                 providerTotals[invoice.providerId].totalCredits += creditAmount;
               }
               totalCredits += creditAmount;
             } else {
               const creditItem = {
                 date: creditMemo.creditDate,
                 amount: creditAmount,
                 description: `Credit for Invoice ${removeLeadingZeros(applied.invoiceNumber)}`,
                 type: 'credit',
                 invoiceNumber: applied.invoiceNumber,
                 patientName: ''
               };
               allCreditItems.push(creditItem);

               // Try to find provider for this invoice
               if (invoiceLookup[applied.invoiceNumber]) {
                 const providerId = invoiceLookup[applied.invoiceNumber].providerId;
                 if (providerId && providerTotals[providerId]) {
                   providerTotals[providerId].totalCredits += creditAmount;
                 }
               }
               totalCredits += creditAmount;
             }
           } else {
             const creditItem = {
               date: creditMemo.creditDate,
               amount: Math.abs(creditMemo.amount),
               description: 'Credit Memo',
               type: 'credit',
               invoiceNumber: '',
               patientName: ''
             };
             allCreditItems.push(creditItem);
             totalCredits += Math.abs(creditMemo.amount);
           }
         });
       } else {
         const creditItem = {
           date: creditMemo.creditDate,
           amount: Math.abs(creditMemo.amount),
           description: 'Credit Memo',
           type: 'credit',
           invoiceNumber: '',
           patientName: ''
         };
         allCreditItems.push(creditItem);
         totalCredits += Math.abs(creditMemo.amount);
       }
     });

     // Group all transactions by provider
     const allTransactions = [...allPaymentItems, ...allCreditItems];
     const transactionsByProvider = groupTransactionsByProvider(allTransactions, invoiceLookup);

     // Assign transactions to provider totals
     transactionsByProvider.forEach(trans => {
       if (trans.providerId && providerTotals[trans.providerId]) {
         providerTotals[trans.providerId].transactions.push(trans);
       } else {
         // Try to find provider from invoice numbers
         let found = false;
         for (const invNum of trans.invoiceNumbers) {
           if (invoiceLookup[invNum] && invoiceLookup[invNum].providerId) {
             const providerId = invoiceLookup[invNum].providerId;
             if (providerId && providerTotals[providerId]) {
               providerTotals[providerId].transactions.push({
                 ...trans,
                 providerId,
                 providerName: providerTotals[providerId].providerName
               });
               found = true;
               break;
             }
           }
         }

         if (!found) {
           // Add to unknown provider
           if (!providerTotals['unknown']) {
             providerTotals['unknown'] = {
               providerId: 'unknown',
               providerName: 'Other Transactions',
               previousBalance: 0,
               newBalance: 0,
               totalDebits: 0,
               totalCredits: 0,
               invoices: [],
               transactions: [],
               oneTo30Days: 0,
               thirtyOneTo60Days: 0,
               sixtyOneTo90Days: 0,
               ninetyPlusDays: 0
             };
           }
           providerTotals['unknown'].transactions.push(trans);
         }
       }
     });

     // Calculate new balance for each provider
     Object.values(providerTotals).forEach(provider => {
       provider.newBalance = provider.totalDebits - provider.totalCredits;
     });

     // Separate previous and current groups
     const previousGroups = [];
     const currentGroups = [];

     Object.values(providerTotals).forEach(provider => {
       if (provider.invoices.some(inv => inv.isPreviousBalance)) {
         previousGroups.push(provider);
       } else if (provider.providerId !== 'unknown') {
         currentGroups.push(provider);
       }
     });

     // Sort transactions within each provider by date
     Object.values(providerTotals).forEach(provider => {
       provider.transactions.sort((a, b) => new Date(a.date) - new Date(b.date));
     });

     // Use aging buckets from the backend
     const oneTo30Days = statement.oneTo30Days || 0;
     const thirtyOneTo60Days = statement.thirtyOneTo60Days || 0;
     const sixtyOneTo90Days = statement.sixtyOneTo90Days || 0;
     const ninetyPlusDays = statement.ninetyPlusDays || 0;
     const previousBalance = statement.previousBalance || 0;
     const newBalance = statement.newBalance || 0;

     // Create groupedByProvider object for rendering
     const groupedByProvider = {};
     Object.values(providerTotals).forEach(provider => {
       if (provider.providerId !== 'unknown' || provider.transactions.length > 0) {
         groupedByProvider[provider.providerId] = {
           providerId: provider.providerId,
           providerName: provider.providerName,
           isPreviousBalance: provider.invoices.some(inv => inv.isPreviousBalance),
           invoices: provider.invoices,
           transactions: provider.transactions,
           totalDebits: provider.totalDebits,
           totalCredits: provider.totalCredits,
           previousBalance: provider.previousBalance,
           newBalance: provider.newBalance,
           oneTo30Days: provider.oneTo30Days,
           thirtyOneTo60Days: provider.thirtyOneTo60Days,
           sixtyOneTo90Days: provider.sixtyOneTo90Days,
           ninetyPlusDays: provider.ninetyPlusDays
         };
       }
     });

     return {
       ...statement,
       groupedByProvider,
       previousGroups,
       currentGroups,
       providerTotals, // Store for later use
       oneTo30Days,
       thirtyOneTo60Days,
       sixtyOneTo90Days,
       ninetyPlusDays,
       previousBalance,
       newBalance,
       totalDebits,
       totalCredits,
       invoiceLookup
     };
   });
 }, []);



  useEffect(() => {
    const fetchLabInfo = async () => {
      try {
        const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/auth/users", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (response.data.success && response.data.users.length > 0) {
          setLabInfo(response.data.users[0]);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);

  useEffect(() => {
    const fetchStatements = async () => {
      if (accountIds.length === 0) {
        setError("No valid account IDs provided.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setProviderMap({});
        setPreviewSaved(false);
        setPreviewSaveMessage("");

        const response = await axios.get("https://hms.backend.hiragroupcloud.com/api/statements/all", {
          params: {
            accountIds: accountIds.join(","),
            date: cutoffDateTime,
            lastRunTime: lastRunTime,
            dontPrintEbilling,
            dontPrintZeroBalance,
            providerIds: providerIds.join(","),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.data.success && Array.isArray(response.data.statements)) {
          const processedStatements = processStatements(response.data.statements);

          setStatements(processedStatements);

          const newProviderMap = {};
          processedStatements.forEach(statement => {
            Object.values(statement.groupedByProvider || {}).forEach(group => {
              if (group.providerId && group.providerName) {
                newProviderMap[group.providerId] = group.providerName;
              }
            });
          });
          setProviderMap(newProviderMap);

          await savePreviewToDatabase(processedStatements);
        } else {
          setError(response.data.message || "No statements data available");
          setStatements([]);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setError(error.response?.data?.message || "An error occurred while fetching statements.");
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatements();
  }, [accountIds, cutoffDateTime, lastRunTime, dontPrintEbilling,
      dontPrintZeroBalance, providerIds, savePreviewToDatabase, processStatements]);

  useEffect(() => {
    if (statements.length === 0 || providerIds.length === 0) {
      setFilteredStatements(statements);
      return;
    }

    const filtered = statements.map(statement => {
      const filteredGroupedByProvider = {};
      Object.entries(statement.groupedByProvider || {}).forEach(([key, group]) => {
        if (providerIds.includes(group.providerId) || providerIds.length === 0) {
          filteredGroupedByProvider[key] = group;
        }
      });

      return {
        ...statement,
        groupedByProvider: filteredGroupedByProvider,
        oneTo30Days: statement.oneTo30Days,
        thirtyOneTo60Days: statement.thirtyOneTo60Days,
        sixtyOneTo90Days: statement.sixtyOneTo90Days,
        ninetyPlusDays: statement.ninetyPlusDays,
        previousBalance: statement.previousBalance,
        newBalance: statement.newBalance
      };
    }).filter(statement =>
      Object.keys(statement.groupedByProvider || {}).length > 0 ||
      !dontPrintZeroBalance
    );

    setFilteredStatements(filtered);
  }, [statements, providerIds, dontPrintZeroBalance]);

 // Modified frontend: handlePrintAndSave function
 const handlePrintAndSave = async () => {
   const statementsToUse = providerIds.length > 0 ? filteredStatements : statements;
   if (statementsToUse.length === 0) {
     setSaveMessage("No statements to save.");
     return;
   }

   try {
     setIsSaving(true);
     setSaveMessage("");
     const statementsToSave = [];
     const runDate = new Date(cutoffDateTime);

     for (const originalStatement of statementsToUse) {
       if (
         dontPrintZeroBalance &&
         Number(originalStatement.newBalance || 0) === 0 &&
         Number(originalStatement.previousBalance || 0) === 0
       ) {
         continue;
       }

       const groupedByProvider = {};
       const providerStatements = [];

       // Process each provider separately
       Object.values(originalStatement.groupedByProvider || {}).forEach((group) => {
         const providerInvoices = [];
         let providerTotalDebit = 0;
         let providerTotalCredit = 0;

         // Process invoices for this provider
         (group.invoices || []).forEach(inv => {
           const patientName = getPatientName(inv);
           const invoiceDate = inv.deliveryDate || inv.invoiceDate;
           const debit = inv.debit || 0;
           const outstanding = inv.outstanding || 0;

           const allInvoicePayments = [...(inv.payments || [])];
           const allInvoiceCredits = [...(inv.credits || [])];

           const totalPaid = allInvoicePayments.reduce((sum, p) => sum + (p.amount || 0), 0);
           const totalCredited = allInvoiceCredits.reduce((sum, c) => sum + (c.amount || 0), 0);

           providerTotalDebit += debit;
           providerTotalCredit += totalPaid + totalCredited;

           const processedInvoice = {
             caseId: inv.caseId || "",
             invoiceNumber: inv.invoiceNumber || "",
             formattedInvoiceNumber: removeLeadingZeros(inv.invoiceNumber),
             deliveryDate: invoiceDate,
             invoiceDate: invoiceDate,
             patientFirstName: inv.patientFirstName || "",
             patientLastName: inv.patientLastName || "",
             patientName: patientName,
             total: debit,
             paid: totalPaid,
             credited: totalCredited,
             debit: debit,
             credit: totalPaid + totalCredited,
             daysUnpaid: inv.daysUnpaid || 0,
             providerName: group.providerName || "Unknown Provider",
             providerId: group.providerId || "",
             originalAmount: debit,
             isPreviousBalance: group.isPreviousBalance || false,
             payments: allInvoicePayments,
             credits: allInvoiceCredits,
             outstanding: outstanding
           };

           providerInvoices.push(processedInvoice);
         });

         // Calculate provider-specific new balance
         const providerNewBalance = group.newBalance ||
           (group.totalDebits || 0) - (group.totalCredits || 0);

         // Create provider-specific statement
         const baseUrl = window.location.origin + "/run-date-range-statement";
         const queryParams = new URLSearchParams({
           accountIds: String(originalStatement.accountId),
           date: cutoffDateTime,
           lastRunTime: lastRunTime || "",
           statementNote: encodeURIComponent(statementNote),
           statementFooter: encodeURIComponent(statementFooter),
           dontPrintEbilling: dontPrintEbilling.toString(),
           dontPrintZeroBalance: dontPrintZeroBalance.toString(),
           autoSaveStatements: "false"
         }).toString();

         const providerStatement = {
           runDate: cutoffDateTime,
           displayDate: displayDate,
           accountId: String(originalStatement.accountId),
           providerName: group.providerName || "Unknown Provider",
           accountHolder: originalStatement.accountHolder || "Unknown",
           address: originalStatement.address || "",
           city: originalStatement.city || "",
           state: originalStatement.state || "",
           zip: originalStatement.zip || "",
           telephone: originalStatement.telephone || "Not available",
           email: originalStatement.email || "Email not available",
           note: statementNote || "",
           footer: statementFooter || "",
           previousBalance: group.previousBalance || 0,
           newBalance: providerNewBalance,
           breakdown: {
             total130: group.oneTo30Days || 0,
             total3160: group.thirtyOneTo60Days || 0,
             total6190: group.sixtyOneTo90Days || 0,
             total91: group.ninetyPlusDays || 0,
             totalCredit: providerTotalCredit,
             totalDebit: providerTotalDebit
           },
           oneTo30Days: group.oneTo30Days || 0,
           thirtyOneTo60Days: group.thirtyOneTo60Days || 0,
           sixtyOneTo90Days: group.sixtyOneTo90Days || 0,
           ninetyPlusDays: group.ninetyPlusDays || 0,
           totalDebit: providerTotalDebit,
           totalCredit: providerTotalCredit,
           accountTotal: providerNewBalance,
           url: `${baseUrl}?${queryParams}`,
           invoices: providerInvoices,
           lastRunDate: originalStatement.lastRunDate || null,
           groupedByProvider: {
             [group.providerId]: {
               providerId: group.providerId,
               providerName: group.providerName,
               isPreviousBalance: group.isPreviousBalance,
               totalDebits: providerTotalDebit,
               totalOutstanding: providerNewBalance,
               invoices: providerInvoices,
               transactions: group.transactions || []
             }
           }
         };

         providerStatements.push(providerStatement);
         groupedByProvider[group.providerId] = providerStatement;
       });

       // Add all provider statements to save list
       statementsToSave.push(...providerStatements);
     }

     if (statementsToSave.length === 0) {
       setSaveMessage("No statements with non-zero balance to save.");
       window.print();
       return;
     }

     const response = await axios.post(
       "https://hms.backend.hiragroupcloud.com/api/statements/save",
       {
         runDate: cutoffDateTime,
         statements: statementsToSave,
         url: window.location.href
       },
       {
         headers: {
           Authorization: `Bearer ${localStorage.getItem("token")}`,
           "Content-Type": "application/json",
         },
       }
     );

     setSaveMessage(`Successfully saved ${response.data.count} statements.`);
     window.print();
   } catch (error) {
     console.error("Save error:", error);
     setSaveMessage(error.response?.data?.message || "Failed to save statements.");
     window.print();
   } finally {
     setIsSaving(false);
   }
 };

  const formatDisplayDate = (dateStr) => {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return "Invalid Date";
    const day = dateObj.getDate().toString().padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = dateObj.getMonth();
    const year = dateObj.getFullYear();
    return `${day} ${monthNames[month]} ${year}`;
  };

  const formatCurrency = (amount) => {
    if (typeof amount !== "number" || isNaN(amount)) return "£0.00";
    return `£${Number(amount).toFixed(2)}`;
  };

  const renderPreviewSaveStatus = () => {
    if (isLoading) return null;
    if (previewSaveError) {
      return (
        <span style={{ color: "orange", fontSize: "12px", marginLeft: "10px" }}>
          ⚠ Preview not saved: {previewSaveError}
        </span>
      );
    }
    return null;
  };

  const displayStatements = (providerIds.length > 0 ? filteredStatements : statements)
    .slice()
    .sort((a, b) => {
      const nameA = (a.accountHolder || "Unknown").toLowerCase();
      const nameB = (b.accountHolder || "Unknown").toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="statement-container">
      {isLoading ? (
        <p>Loading statements...</p>
      ) : displayStatements.length === 0 ? (
        <div>
          <p>No statements available for the selected criteria.</p>
          {providerIds.length > 0 && (
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "10px" }}>
              No invoices found for the selected providers.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="header-controls">
            <div className="button-container">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <button className="print-button" onClick={handlePrintAndSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Print & Save"}
                </button>
                {renderPreviewSaveStatus()}
              </div>
              {saveMessage && <p className="save-message">{saveMessage}</p>}
            </div>
          </div>

          {displayStatements.map((statement, statementIndex) => {
            // Get all providers except unknown
            const providerList = Object.values(statement.groupedByProvider || {})
              .filter(group => group.providerId !== 'unknown')
              .sort((a, b) => (a.providerName || '').localeCompare(b.providerName || ''));

            // Calculate total for all providers
            const totalNewBalance = providerList.reduce((sum, p) => sum + (p.newBalance || 0), 0);

            return (
              <div
                key={statement.accountId}
                className={
                  hideZeroBalanceInPrint &&
                  Number(statement.newBalance || 0) === 0 &&
                  Number(statement.previousBalance || 0) === 0
                    ? "hide-in-print"
                    : ""
                }
                style={{ marginBottom: "40px", pageBreakBefore: statementIndex > 0 ? "always" : "auto" }}
              >
                <div className="preview-watermark">Preview <br/> Only</div>

                <div className="header-section">
                  <div className="lab-info">
                    <strong>{labInfo?.labName || "Lab Name"}</strong>
                    <br />
                    {labInfo?.address || "Address not available"}
                    <br />
                    {labInfo?.city}, {labInfo?.state} {labInfo?.zipCode}
                    <br />
                  </div>
                  <div className="header-right">
                    <div className="statement-info">
                      <div className="statement-date">
                        <strong>Statement for {formatDisplayDate(displayDate)}</strong>
                      </div>
                      <div className="contact-info">
                        {labInfo?.telephone || "Not available"}
                        <br />
                        {labInfo?.labEmail || "Email not available"}
                      </div>
                    </div>
                    <div className="logo-container-right">
                      <img src="./log.png" alt="" className="header-logo" />
                    </div>
                  </div>
                </div>

                <h3 className="statement-title">Statement for {formatDisplayDate(displayDate)}</h3>

                <div className="client-payment-section">
                  <div className="client-info">
                    <strong>{statement.accountHolder}</strong>
                    <br />
                    {statement.address || "Address not available"}
                    <br />
                    {[statement.city, statement.state, statement.zip].filter(Boolean).join(", ")}
                  </div>
                  <div className="payment-details">
                    {statementNote ? (
                      <div style={{ whiteSpace: "pre-line", fontSize: "13px" }}>{statementNote}</div>
                    ) : (
                      <div>
                        <div>Telephone: {statement.telephone || "Not available"}</div>
                        <div>Email: {statement.email || "Email not available"}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Debit</th>
                        <th>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providerList.map((provider) => {
                        const lines = [];

                        // Separate previous and current invoices for this provider
                        const prevInvoices = provider.invoices.filter(inv => inv.isPreviousBalance);
                        const currentInvoices = provider.invoices.filter(inv => !inv.isPreviousBalance);

                        // Add previous balance if exists
                        if (prevInvoices.length > 0) {
                          const prevTotal = prevInvoices.reduce((sum, inv) => sum + (inv.debit || 0), 0);
                          const prevDateStr = statement.lastRunDate || '';
                          lines.push({
                            dateStr: formatDisplayDate(prevDateStr),
                            dateObj: new Date(prevDateStr || new Date()),
                            desc: 'Previous Balance',
                            debit: formatDebit(prevTotal),
                            credit: '',
                            sortDate: new Date(prevDateStr || '1970-01-01')
                          });
                        }

                        // Add current invoices
                        currentInvoices.forEach(invoice => {
                          const patientName = getPatientName(invoice);
                          const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
                          const formattedInvoiceNumber = removeLeadingZeros(invoice.invoiceNumber);
                          const debit = invoice.debit || 0;

                          lines.push({
                            dateStr: formatDisplayDate(invoiceDate),
                            dateObj: new Date(invoiceDate),
                            desc: `Case Invoice ${formattedInvoiceNumber} / ${patientName}`,
                            debit: formatDebit(debit),
                            credit: '',
                            sortDate: new Date(invoiceDate)
                          });
                        });

                        // Add provider-specific transactions (payments/credits)
                        provider.transactions.forEach(trans => {
                          lines.push({
                            dateStr: formatDisplayDate(trans.date),
                            dateObj: new Date(trans.date),
                            desc: trans.displayDescription,
                            debit: '',
                            credit: formatCredit(trans.amount),
                            sortDate: new Date(trans.date)
                          });
                        });

                        // Sort all lines by date
                        lines.sort((a, b) => a.sortDate - b.sortDate);

                        return (
                          <React.Fragment key={provider.providerId}>
                            <tr className="provider-row">
                              <td colSpan="4">
                                <strong>{provider.providerName}</strong>
                              </td>
                            </tr>
                            {lines.length > 0 ? (
                              lines.map((line, lineIdx) => (
                                <tr key={`${provider.providerId}-line-${lineIdx}`}>
                                  <td>{line.dateStr}</td>
                                  <td>{line.desc}</td>
                                  <td>{line.debit}</td>
                                  <td>{line.credit}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                  No transactions for this provider
                                </td>
                              </tr>
                            )}
                            {/* Provider subtotal */}
                            <tr className="subtotal-row">
                              <td colSpan="2"><strong>Subtotal </strong></td>
                              <td>{formatDebit(provider.newBalance || 0)}</td>
                              <td></td>
                            </tr>
                          </React.Fragment>
                        );
                      })}

                    </tbody>
                  </table>
                </div>

                <div className="table-wrapper">
                  <table className="statement-table center">
                    <thead>
                      <tr>
                        <th>1-30 Days</th>
                        <th>31-60 Days</th>
                        <th>61-90 Days</th>
                        <th>91+ Days</th>
                        <th>New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{formatCurrency(statement.oneTo30Days)}</td>
                        <td>{formatCurrency(statement.thirtyOneTo60Days)}</td>
                        <td>{formatCurrency(statement.sixtyOneTo90Days)}</td>
                        <td>{formatCurrency(statement.ninetyPlusDays)}</td>
                        <td>{formatCurrency(statement.newBalance)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="statement-footer">
                  <div>{statement.accountHolder}</div>
                  <div>{statementFooter}</div>
                  <div>Page {statementIndex + 1}</div>
                </div>
              </div>
            );
          })}
        </>
      )}

      <style>{`
        .statement-container {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 14px;
          color: #000;
          padding: 20px;
          max-width: 100%;
          margin: auto;
          position: relative;
          box-sizing: border-box;
        }
        .error-message {
          color: red;
          padding: 20px;
          font-weight: bold;
        }
        .header-controls {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 20px;
        }
        .button-container {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }
        .print-button {
          background-color: #004aad;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .print-button:hover {
          background-color: #003a8c;
        }
        .print-button:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        .save-message {
          margin: 0;
          color: green;
          font-size: 14px;
        }
        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: nowrap;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
          margin-bottom: 20px;
          width: 100%;
          gap: 10px;
        }
        .lab-info {
          flex: 1;
          text-align: left;
          min-width: 150px;
          white-space: nowrap;
        }
        .header-right {
          display: flex;
          align-items: flex-start;
          gap: 50px;
        }
        .statement-info {
          text-align: left;
          font-size: 13px;
          white-space: nowrap;
        }
        .statement-date {
          font-size: 15px;
        }
        .contact-info {
          font-size: 13px;
        }
        .logo-container-right {
          display: flex;
          justify-content: flex-end;
        }
        .header-logo {
          height: 50px;
          max-width: 120px;
          object-fit: contain;
        }
        .statement-title {
          text-align: right;
          margin-top: 0;
          margin-bottom: 30px;
        }
        .client-payment-section {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          margin-bottom: 20px;
          gap: 20px;
        }
        .client-info {
          font-size: 13px;
        }
        .payment-details {
          max-width: 100%;
          font-size: 13px;
          text-align: left;
        }
        .table-wrapper {
          overflow-x: auto;
          margin-bottom: 30px;
        }
        .statement-table {
          width: 100%;
          border-collapse: collapse;
        }
        .statement-table th,
        .statement-table td {
          border: 1px solid #000;
          padding: 4px;
        }
        .statement-table th {
          background-color: #004aad;
          color: white;
          font-weight: bold;
          font-size: 13px;
          text-align: center;
        }
        .statement-table.center th {
          text-align: center;
        }
        .statement-table.center td {
          text-align: right;
        }
        .statement-table.center td:nth-child(2) {
          text-align: right;
        }
        .statement-table td:nth-child(2) {
          text-align: left;
        }
        .statement-table td:nth-child(3) {
          text-align: right;
        }
        .statement-table td:nth-child(4) {
          text-align: right;
        }
        .provider-row {
          font-weight: bold;
        }
        .provider-row td {
          padding: 4px 6px;
        }
        .statement-table .subtotal-row td {
          text-align: right;
          font-weight: bold;
        }
        .statement-table .total-row td {
          text-align: right;
          font-weight: bold;
          border-top: 2px solid #000;
        }
        .statement-footer {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #444;
        }
        .preview-watermark {
          position: absolute;
          top: 269px;
          left: 47%;
          transform: translate(-50%, -50%);
          font-size: 65px;
          font-weight: 600;
          color: #c0c0c0;
          opacity: 0.35;
          line-height: 1.1;
          z-index: 1;
          pointer-events: none;
        }
        .sub-row td {
          font-size: 12px;
          border-top: none;
          font-style: normal !important;
          font-weight: normal !important;
        }
       
        @media screen and (max-width: 768px) {
          .header-section {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .lab-info,
          .statement-info,
          .logo-container-right {
            text-align: center;
            width: 100%;
            margin-bottom: 10px;
          }
          .header-right {
            justify-content: center;
            gap: 20px;
          }
          .contact-info {
            margin-right: 0;
          }
          .client-payment-section {
            flex-direction: column;
          }
          .payment-details {
            text-align: left;
          }
        }
       
        @media print {
          .header-controls, .print-button, .close-button {
            display: none !important;
          }
          .statement-container {
            padding: 0;
          }
          .table-wrapper {
            overflow-x: visible;
          }
          .statement-table {
            page-break-inside: avoid;
          }
          .statement-table th {
            color: #000 !important;
            background-color: #fff !important;
            border: 1px solid #000 !important;
          }
          .statement-table td {
            border: 1px solid #000 !important;
          }
          .preview-watermark {
            display: none !important;
          }
          .hide-in-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReprintButton;